import { nanoid } from 'nanoid';

import type { Length } from '../../types';
import { convertLength, mapObjectValues } from '../../util';
import type {
  CreateParagraphArgs,
  SuggestedTableRowEdit,
  TableBorder,
  TableBorders,
  TableCellElement,
  TableCellMargins,
  TableElement,
  TableRowElement,
} from '../internal';
import {
  createDefaultParagraph,
  EditingMode,
  generateLocationId,
  tableCellType,
  tableRowType,
  tableType,
} from '../internal';

/**
 * Default border style used during rendering for tables that lack explicit borders. This is for
 * backwards compatibility only, since tables have no borders by default in MS Word.
 */
export const defaultBorder: TableBorder = {
  color: 'black',
  size: 0.75,
  style: 'solid',
};

export const noBorder: TableBorder = {
  color: 'black',
  size: 0,
  style: 'none',
};

/**
 * Default border style used during parsing.
 */
export const defaultParsedBorder = noBorder;

export const defaultTableBorders: TableBorders = {
  bottom: defaultBorder,
  insideH: defaultBorder,
  insideV: defaultBorder,
  left: defaultBorder,
  right: defaultBorder,
  top: defaultBorder,
};

export const noTableBorders: TableBorders = {
  bottom: noBorder,
  insideH: noBorder,
  insideV: noBorder,
  left: noBorder,
  right: noBorder,
  top: noBorder,
};

export const defaultParsedTableBorders = noTableBorders;

export const defaultTableCellMargins: TableCellMargins<Length.Twip> = {
  bottom: { type: 'tw', value: 0 },
  left: { type: 'tw', value: 115 },
  right: { type: 'tw', value: 115 },
  top: { type: 'tw', value: 0 },
};

export const noTableCellMargins: TableCellMargins<Length.Nil> = {
  bottom: { type: 'nil' },
  left: { type: 'nil' },
  right: { type: 'nil' },
  top: { type: 'nil' },
};

export const defaultTableCellMarginsIn: TableCellMargins<number> = mapObjectValues(
  defaultTableCellMargins,
  l => convertLength(l, 'in')
);

export const minimumTableCellWidthIn =
  0.09 + defaultTableCellMarginsIn.left + defaultTableCellMarginsIn.right;

/**
 * Respect table styling/formats when creating table elements such as a row or cell(column)
 * @url https://arity.atlassian.net/browse/AL-1900
 */

export function createDefaultTableElement({
  authorId,
  columnCount,
  contentWidthIn,
  editingMode,
  groupId,
  rowCount,
  tableBorders,
}: {
  authorId: string;
  columnCount: number;
  /* Width to fit the table on *in inches* */
  contentWidthIn: number;
  editingMode: EditingMode;
  rowCount: number;
  groupId?: string;
  tableBorders?: TableBorders;
}): TableElement {
  const tableWidthIn = Math.max(columnCount * minimumTableCellWidthIn, contentWidthIn);
  const tableElement: TableElement = {
    align: 'left',
    borders: tableBorders ?? defaultTableBorders,
    children: [],
    indent: { type: 'nil' },
    locationId: generateLocationId(),
    suggestedEdits: [],
    type: tableType,
    width: tableWidthIn,
  };
  const suggestedEdits: SuggestedTableRowEdit[] = [];

  if (editingMode === EditingMode.SUGGESTING) {
    suggestedEdits.push({
      authorId,
      groupId: groupId ?? nanoid(),
      type: 'insertTableRow',
      value: {},
    });
  }

  // Add requested amount of rows.
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    tableElement.children.push({
      ...createDefaultTableRowElement({ columnCount }),
      suggestedEdits,
    });
  }

  return tableElement;
}

interface CreateCellArgs extends CreateParagraphArgs {
  width: TableCellElement['width'];
}

export function createDefaultTableCellElement({
  width,
  ...paragraphArgs
}: CreateCellArgs): TableCellElement {
  return {
    children: [createDefaultParagraph(paragraphArgs)],
    locationId: generateLocationId(),
    suggestedEdits: [],
    type: tableCellType,
    width,
  };
}

export function createDefaultTableRowElement({
  columnCount,
}: {
  columnCount: number;
}): TableRowElement {
  return {
    cantSplit: false,
    children: Array.from({ length: columnCount }, () =>
      createDefaultTableCellElement({ width: { type: 'auto' } })
    ),
    locationId: generateLocationId(),
    suggestedEdits: [],
    type: tableRowType,
  };
}
